import { useNavigate } from 'react-router-dom';
import { Save, LogOut, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { AuthorizationsAccessMatrix } from '../components/authorizations/AuthorizationsAccessMatrix';
import { AuthorizationsListPanel } from '../components/authorizations/AuthorizationsListPanel';
import { AuthorizationsRoleScreenMatrix } from '../components/authorizations/AuthorizationsRoleScreenMatrix';
import { useAuthorizationsConfigPage } from '../hooks/useAuthorizationsConfigPage';
import { formatDateTime } from '../utils/formatDate';

const TABS = [
  { id: 'principals', label: 'Roles & emails' },
  { id: 'screens', label: 'Screens' },
  { id: 'endpoints', label: 'API endpoints' },
];

export default function AuthorizationsConfigPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const vm = useAuthorizationsConfigPage();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="authorizations-config-page">
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-[#003380]" />
            <div>
              <h1 className="text-xl font-bold govt-blue-text font-serif">Authorisations</h1>
              <p className="text-sm text-slate-600">Screen &amp; API access matrix</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-1">
              <p className="text-sm font-medium text-slate-800">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">
                {user?.role?.replace(/_/g, ' ')}
              </p>
            </div>
            <Button
              onClick={vm.handleSave}
              disabled={vm.loading || vm.saving}
              data-testid="save-auth-btn"
            >
              <Save className="mr-2 h-4 w-4" /> {vm.saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 p-6 space-y-4">
        <p className="max-w-3xl text-sm text-slate-500">
          Elevated roles/emails bypass checks and may manage this page. Screen grants sync related
          APIs automatically when you save.
        </p>

        {vm.loading ? (
          <p className="text-sm text-slate-500">Loading authorisations…</p>
        ) : (
          <>
            {vm.config?.updated_at ? (
              <p className="text-xs text-slate-400">
                Last updated {formatDateTime(vm.config.updated_at)}
                {vm.config.updated_by ? ` by ${vm.config.updated_by}` : ''}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => vm.setTab(tab.id)}
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    vm.tab === tab.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`auth-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {vm.tab === 'principals' ? (
              <div className="grid gap-4 lg:grid-cols-2">
                <AuthorizationsListPanel
                  title="Elevated roles"
                  description="These roles bypass the matrix and can open Authorisations (default: super_admin)."
                  draft={vm.roleDraft}
                  items={vm.roles}
                  placeholder="e.g. super_admin"
                  addLabel="Add role"
                  testIdPrefix="auth-roles"
                  onDraftChange={vm.setRoleDraft}
                  onAdd={vm.addRole}
                  onRemove={vm.removeRole}
                />
                <AuthorizationsListPanel
                  title="Elevated emails"
                  description="These emails bypass matrix checks and may manage authorisations."
                  draft={vm.emailDraft}
                  items={vm.emails}
                  placeholder="e.g. admin@iith.ac.in"
                  addLabel="Add email"
                  testIdPrefix="auth-emails"
                  onDraftChange={vm.setEmailDraft}
                  onAdd={vm.addEmail}
                  onRemove={vm.removeEmail}
                />
              </div>
            ) : null}

            {vm.tab === 'screens' ? (
              <AuthorizationsRoleScreenMatrix
                title="Screens by role"
                description="Rows are all portal roles; columns are screens. Checking a screen also grants related APIs."
                roles={vm.matrixRoles}
                screens={vm.screens}
                filter={vm.screenFilter}
                onFilterChange={vm.setScreenFilter}
                onToggle={vm.toggleScreenRole}
                onSetAllScreensForRole={vm.setAllScreensForRole}
                testIdPrefix="auth-screens"
              />
            ) : null}

            {vm.tab === 'endpoints' ? (
              <AuthorizationsAccessMatrix
                title="API endpoints"
                description="Auto-filled from Screens. Fine-tune manually if needed."
                roles={vm.matrixRoles}
                rows={vm.endpoints}
                filter={vm.endpointFilter}
                onFilterChange={vm.setEndpointFilter}
                onToggle={vm.toggleEndpointRole}
                onSetAllRoles={vm.setAllEndpointRoles}
                testIdPrefix="auth-endpoints"
                showMethod
              />
            ) : null}

            <p className="text-xs text-amber-700/90">
              Your own role or email must remain in the elevated list when saving.
            </p>
          </>
        )}
        </main>
      </div>
    </div>
  );
}
